import { useEffect, useRef, useState } from "react";

import styles from "@styles/forms-flow/creative.module.scss";

import {
  Button,
  Drawer,
  Input,
  Label,
  Loading,
  Steps as StepsComponent,
} from "@components";

import { CreativeName } from "./components/CreativeName";
import { SmsMessage } from "./components/SmsMessage";
import { UrlType } from "./components/UrlType";

import CreativeDetails from "@containers/flows/CreativeDetails";
import { FlowAPI } from "@providers/FlowAPI";
import { FlowStorage } from "@storage/FlowStorage";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import { ContemplationInterval } from "./components/ContemplationInterval";
import { If } from "shared/components/control-statements";
import Coupons from "./components/Coupons";

const Steps = {
  NAME: 1,
  SENDER: 2,
  URL: 3,
  CONTEMPLATION: 4,
  BENEFIT_TYPE: 5,
  CHOOSE_BENEFIT: 6,
  SMS_MESSAGE: 7,
  FINISHED: 8,
};

const options = {
  REPORT: "REPORT",
  FORM: "FORM",
};

export const SmsCreativeActionForm = ({
  visible,
  flowType,
  onClose,
  node,
  restrictions,
  onChange,
  onSave,
  isCreativeAction,
}) => {
  const [steps, setSteps] = useState(Object.keys(Steps).length);
  const [currentStep, setCurrentStep] = useState(Steps.NAME);
  const [creative, setCreative] = useState({
    creative: node?.data?.creative || {},
    coupon: node?.data?.coupon || {},
  });
  const [isValid, setIsValid] = useState(false);
  const [brandsList, setBrands] = useState([]);
  const [showStartOptions, setShowStartOptions] = useState(false);
  const [step, setStep] = useState(null);
  const [report, setReport] = useState({});
  const [maxGainEnabled, setMaxGainEnabled] = useState(false);
  const [environment, setEnvironment] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!creative?.creative?.senderName && currentStep === Steps.SENDER)
      setCreative({
        ...creative,
        creative: {
          ...creative.creative,
          senderName: `${environment?.brand?.name || environment.name}` || "",
        },
      });
  }, [currentStep]);

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, [creative]);

  const getFreshNode = (data) => {
    return {
      ...node,
      data: {
        ...node.data,
        ...data,
      },
    };
  };

  useEffect(() => {
    if (restrictions?.lockCreativeBenefit) {
      setSteps(
        Object.values(Steps).filter((stepIndex) =>
          [Steps.NAME, Steps.SENDER, Steps.URL, Steps.SMS_MESSAGE].includes(
            stepIndex
          )
        ).length
      );
    }
  }, [restrictions]);

  useEffect(() => {
    const c = {
      creative: node?.data?.creative || {},
      coupon: node?.data?.coupon || {},
    };
    setCreative(c);
    setIsValid(isValidStep(currentStep, c, flowType, maxGainEnabled));
  }, [node]);

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, [currentStep]);

  const onChangeCreative = (branch, value) => {
    const newCreative = {
      ...creative,
      [branch]: {
        ...(creative[branch] || {}),
        ...value,
      },
    };

    if (
      newCreative?.creative?.senderName &&
      !newCreative?.creative?.senderName.includes(":")
    )
      newCreative.creative.senderName = `${newCreative?.creative?.senderName}:`;

    setCreative(newCreative);
    setIsValid(isValidStep(currentStep, newCreative, flowType, maxGainEnabled));
    onChange && onChange(getFreshNode(newCreative));
    return getFreshNode(newCreative);
  };

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, []);

  useEffect(() => {
    if (isCreativeAction) setShowStartOptions(true);
  }, [isCreativeAction]);

  const nextStep = (futureCreative) => {
    const nextStep = getNextStep(
      currentStep,
      futureCreative || creative,
      restrictions,
      brandsList
    );
    onSave && onSave(getFreshNode(futureCreative || creative), false);

    return nextStep;
  };

  const loadReport = async (flowId, stepId, creativeId) => {
    setLoading(true);
    const response = await FlowAPI.getFlowReports(flowId, stepId);
    const allCreatives = response.getData({ creatives: [] }).creatives;
    const selectedCreative = allCreatives.filter((c) => c.id === creativeId);

    setReport(selectedCreative[0]);
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      const stepId = node?.data?.stepId;
      const flowId = node?.data?.flow_id;
      const creativeId = node?.data?.creative?.id;

      loadReport(flowId, stepId, creativeId);
    }
  }, [node?.data?.stepId]);

  useEffect(() => {
    setEnvironment(EnvironmentStorage.getEnvironment());
  }, []);

  const creativeName = useRef(creative?.creative?.name);
  const startedCreative = creativeName.current;

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        onClose();
        setCurrentStep(Steps.NAME);

        if (showStartOptions) setStep(null);
      }}
      showBackButton={
        (currentStep > Steps.NAME && currentStep != Steps.FINISHED) ||
        (step && showStartOptions)
      }
      backButton={() =>
        step && currentStep === Steps.NAME
          ? setStep(null)
          : setCurrentStep((prevState) =>
              getPrevStep(prevState, creative, restrictions)
            )
      }
      title={"Adicionar um criativo - SMS"}
    >
      {showStartOptions && !step && !!startedCreative && (
        <>
          <Label>O que deseja fazer?</Label>

          <div className={styles.options}>
            <div
              className={styles.option}
              onClick={() => setStep(options.REPORT)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <i className="fal fa-list" />
                </div>
                <p>Ver relatório</p>
              </div>
              <div className={styles.chevron}>
                <i className="fas fa-chevron-right" />
              </div>
            </div>
            <div
              className={styles.option}
              onClick={() => setStep(options.FORM)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <i className="fal fa-pen-to-square" />
                </div>
                <p>Editar disparo</p>
              </div>
              <div className={styles.chevron}>
                <i className="fas fa-chevron-right" />
              </div>
            </div>
          </div>
        </>
      )}

      {step === options.REPORT && (
        <>
          <If cond={loading}>
            <div className={styles.loadingReports}>
              <Loading />
            </div>
          </If>

          <If cond={!loading}>
            <CreativeDetails
              key={`creative-details-${Date.now()}`}
              flowType={FlowStorage.getFlowType(flowType)}
              creative={creative}
              node={node}
              report={report}
            />
          </If>
        </>
      )}

      {(((step === options.FORM || !showStartOptions) && startedCreative) ||
        !startedCreative) && (
        <>
          <StepsComponent
            steps={steps}
            stepsIsArray
            currentStep={currentStep}
          />

          <div className={styles.scrollContainer}>
            {currentStep === Steps.NAME && (
              <CreativeName
                node={node}
                flowType={flowType}
                creative={creative?.creative}
                restrictions={restrictions}
                onChangeCreative={(data) => onChangeCreative("creative", data)}
              />
            )}

            {currentStep === Steps.SENDER && (
              <>
                <Label>Defina o remetente</Label>
                <Input
                  placeholder="Nome do remetente"
                  autoFocus
                  uppercase
                  value={creative?.creative?.senderName?.replace(/:/g, "")}
                  onChange={(data) =>
                    onChangeCreative("creative", {
                      senderName: data.target.value.replace(/:/g, ""),
                    })
                  }
                />
              </>
            )}

            {currentStep === Steps.URL && (
              <UrlType
                restrictions={restrictions}
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.CONTEMPLATION && (
              <ContemplationInterval
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.CHOOSE_BENEFIT && (
              <Coupons
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.SMS_MESSAGE && (
              <SmsMessage
                coupon={creative.coupon}
                senderName={creative?.creative?.senderName}
                url={creative?.creative?.url}
                message={creative.creative?.message}
                restrictions={restrictions}
                onChangeCreative={(data) =>
                  onChangeCreative("creative", { message: data })
                }
                nextStep={() => setCurrentStep(nextStep())}
              />
            )}

            {currentStep === Steps.FINISHED && (
              <div className={styles.finished}>
                <i className="far fa-check" />
                <p>Seu disparo foi configurado com sucesso!</p>
              </div>
            )}
          </div>

          <If cond={currentStep !== Steps.SMS_MESSAGE}>
            <div className={styles.footer}>
              <div className="d-flex flex-column w-100">
                {currentStep !== Steps.FINISHED && (
                  <>
                    {currentStep === Steps.BENEFIT_TYPE && (
                      <Button
                        className="w-100 mb-15"
                        design="default-2"
                        onClick={() => {
                          const futureCreative = onChangeCreative("coupon", {
                            type: null,
                          });
                          setCurrentStep(nextStep(futureCreative));
                        }}
                      >
                        Não conceder benefícios
                      </Button>
                    )}

                    {currentStep === Steps.URL && (
                      <Button
                        className="w-100 mb-15"
                        design="default-2"
                        onClick={() => {
                          const futureCreative = onChangeCreative("creative", {
                            url: {
                              type: null,
                              url: null,
                            },
                          });
                          setCurrentStep(nextStep(futureCreative));
                        }}
                      >
                        Não inserir CTA
                      </Button>
                    )}

                    {currentStep === Steps.SENDER && (
                      <Button
                        className="w-100 mb-15"
                        design="default-2"
                        onClick={() => {
                          const futureCreative = onChangeCreative("creative", {
                            senderName: null,
                          });
                          setCurrentStep(nextStep(futureCreative));
                        }}
                      >
                        Continuar sem remetente
                      </Button>
                    )}

                    <Button
                      className="w-100"
                      disabled={!isValid}
                      onClick={() => setCurrentStep(nextStep())}
                      design="primary"
                    >
                      Próximo
                    </Button>
                  </>
                )}

                {currentStep === Steps.FINISHED && (
                  <Button
                    className="w-100"
                    onClick={() => {
                      setCurrentStep(Steps.NAME);
                      setCreative({});
                      onClose();
                    }}
                  >
                    Voltar para fluxos
                  </Button>
                )}
              </div>
            </div>
          </If>
        </>
      )}
    </Drawer>
  );
};

const getNextStep = (
  currentStep,
  { creative, coupon },
  restrictions,
  brandsList = []
) => {
  if (restrictions?.lockCreativeBenefit == true) {
    switch (currentStep) {
      case Steps.NAME:
        return Steps.SENDER;
      case Steps.SENDER:
        return Steps.URL;
      case Steps.URL:
        return Steps.SMS_MESSAGE;
      case Steps.SMS_MESSAGE:
        return Steps.FINISHED;
      default:
        return currentStep;
    }
  }

  switch (currentStep) {
    case Steps.NAME:
      return Steps.SENDER;
    case Steps.SENDER:
      return Steps.URL;
    case Steps.URL:
      return Steps.CONTEMPLATION;
    case Steps.CONTEMPLATION:
      return Steps.CHOOSE_BENEFIT;
    case Steps.CHOOSE_BENEFIT:
      return Steps.SMS_MESSAGE;
    case Steps.SMS_MESSAGE:
      return Steps.FINISHED;
    case Steps.GAIN_REMINDER:
      if (coupon.expiration?.gain?.remind?.active) {
        return Steps.GAIN_REMINDER_MESSAGE;
      }
      return Steps.FINISHED;
    case Steps.GAIN_REMINDER_MESSAGE:
      return Steps.FINISHED;
    default:
      return currentStep;
  }
};

const getPrevStep = (
  currentStep,
  { creative, coupon },
  restrictions,
  brandsList
) => {
  if (restrictions?.lockCreativeBenefit == true) {
    switch (currentStep) {
      case Steps.NAME:
        return null;
      case Steps.SENDER:
        return Steps.NAME;
      case Steps.URL:
        return Steps.SENDER;
      case Steps.SMS_MESSAGE:
        return Steps.URL;
      default:
        return currentStep;
    }
  }

  switch (currentStep) {
    case Steps.NAME:
      return null;
    case Steps.SENDER:
      return Steps.NAME;
    case Steps.URL:
      return Steps.SENDER;
    case Steps.CONTEMPLATION:
      return Steps.URL;
    case Steps.CHOOSE_BENEFIT:
      return Steps.CONTEMPLATION;
    case Steps.SMS_MESSAGE:
      return Steps.CHOOSE_BENEFIT;
    case Steps.GAIN_REMINDER:
      return Steps.SMS_MESSAGE;
    default:
      return currentStep;
  }
};

const isValidStep = (currentStep, data, flowType, maxGainEnabled) => {
  const { creative, coupon } = data;

  switch (currentStep) {
    case Steps.NAME:
      return !!creative?.name && !!(flowType == "EVENT" || creative?.sendTime);
    case Steps.CONTEMPLATION:
      return !!creative?.contemplation && !!creative?.contemplation?.type;
    case Steps.SENDER:
      return !!creative?.senderName;
    case Steps.URL:
      return (
        creative?.url?.type == "STANDARD" ||
        (creative?.url?.type == "CUSTOM" && creative?.url?.url)
      );
    case Steps.SMS_MESSAGE:
      return !!creative?.message?.template;
    case Steps.GAIN_REMINDER: {
      const { active, days, time } = coupon?.expiration?.gain?.remind || {};
      return active === false || (!!days && !!time);
    }
    case Steps.GAIN_REMINDER_MESSAGE:
      return !!coupon?.expiration?.gain?.remind?.message;
  }

  return true;
};
